import React, { FormEvent, useEffect, useState } from 'react';

import { MtxButton, MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { usePostAppAccountPasswordvalidateInvoke } from '@/api';
import { Loader } from '@/components/Loader/loader';
import TextInput from '@/components/TextInput/TextInput';
import { ROUTE } from '@/constants/route';

import styles from './styles.module.css';

export const ReinitialisationMdp: React.FC = () => {
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [inputLabel, setInputLabel] = useState('');

  const [searchParams] = useSearchParams();
  const pageKey = searchParams.get('page');
  const tokenKey = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  const [isErrorPassword, setIsErrorPassword] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [type, setType] = useState('password');
  const [typeConfirm, setTypeConfirm] = useState('password');

  const [mdpIsValidCapital, setMdpIsValidCapital] = useState(false);
  const [mdpIsValidLower, setMdpIsValidLower] = useState(false);
  const [mdpIsValidLength, setMdpIsValidLength] = useState(false);
  const [mdpIsValidDigit, setMdpIsValidDigit] = useState(false);
  const [mdpIsValidSpecialChar, setMdpIsValidSpecialChar] = useState(false);

  const [showLoader, setShowLoader] = useState(false);

  const mdpRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[ -/:@[\]`{-~]).{8,}$/;

  const capitalRegex = /(?=.*[A-Z])/;
  const lowerRegex = /(?=.*[a-z])/;
  const lengthRegex = /(?=.{8,}$)/;
  const digitRegex = /\d/;
  const specialCharRegex = /[ -/:-@[-`{-~]/;
  const validationNewmdp = usePostAppAccountPasswordvalidateInvoke();

  const navigate = useNavigate();

  const handleToggle = () => {
    if (type === 'password') {
      setType('text');
    } else {
      setType('password');
    }
  };

  const handlePasswordInput = (e: FormEvent<HTMLInputElement>) => {
    const inputPassword = e.currentTarget.value;
    setPassword(e.currentTarget.value);

    if (capitalRegex.test(inputPassword)) setMdpIsValidCapital(true);
    else setMdpIsValidCapital(false);

    if (lowerRegex.test(inputPassword)) setMdpIsValidLower(true);
    else setMdpIsValidLower(false);

    if (lengthRegex.test(inputPassword)) setMdpIsValidLength(true);
    else setMdpIsValidLength(false);

    if (digitRegex.test(inputPassword)) setMdpIsValidDigit(true);
    else setMdpIsValidDigit(false);

    if (specialCharRegex.test(inputPassword)) setMdpIsValidSpecialChar(true);
    else setMdpIsValidSpecialChar(false);
  };

  const handleToggleConfirm = () => {
    if (typeConfirm === 'password') {
      setTypeConfirm('text');
    } else {
      setTypeConfirm('password');
    }
  };

  const validatePasswords = () => {
    if (mdpRegex.test(password)) {
      if (password === confirmPassword) {
        setIsErrorPassword(false);
        setErrorText(null);
        return true;
      } else {
        setIsErrorPassword(true);
        setErrorText('Les mots de passe ne correspondent pas.');
        setShowLoader(false);
        return false;
      }
    } else {
      setIsErrorPassword(true);
      setErrorText('Le mot de passe ne respecte pas la consigne.');
      setShowLoader(false);
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowLoader(true);

    if (validatePasswords()) {
      validationNewmdp.mutate(
        { data: { token: tokenKey, mdp: password } },
        {
          onSuccess: (data) => {
            navigate(ROUTE.VALIDATIONMODIFICATIONMDP, {
              state: { page: pageKey, success: data.succes, newMdp: password },
            });
          },
          onError: () => {
            navigate(ROUTE.ERREUR);
          },
        }
      );
    }
  };

  const handleCancel = () => {
    navigate(ROUTE.LOGIN);
  };

  useEffect(() => {
    switch (pageKey) {
      case 'init':
        setTitle('Créez votre mot de passe');
        setSubTitle('Vous devez définir un mot de passe pour vous connecter.');
        setInputLabel('Choisissez votre mot de passe');
        break;

      case 'reset':
      default:
        setTitle('Saisissez un nouveau mot de passe');
        setSubTitle('Vous devez définir un nouveau mot de passe pour vous connecter.');
        setInputLabel('Nouveau mot de passe');
        break;
    }
  }, [pageKey]);

  const getPasswordIcon = () => {
    if (isErrorPassword) return '';
    if (type === 'password') {
      return 'totem-icon totem-icon-eye';
    } else {
      return 'totem-icon totem-icon-eye-closed';
    }
  };

  return (
    <MtxGrid>
      {showLoader && <Loader />}
      <MtxGrid row>
        <MtxGrid col colLg={6} offsetLg={3} offsetSm={2} colSm={8} colXs={12}>
          <div className={styles.titleSection}>
            <h1>{title}</h1>
            <p>{subTitle}</p>
          </div>

          <form
            onSubmit={(e: React.FormEvent) => {
              handleSubmit(e);
            }}
          >
            <div>
              <div>
                <TextInput
                  label={inputLabel}
                  labelFor="mdp"
                  placeholder="Votre nouveau mot de passe"
                  type={type}
                  value={password}
                  img={isErrorPassword ? 'totem-icon totem-icon-cross-circle' : ''}
                  icon={getPasswordIcon()}
                  textError={null}
                  isError={isErrorPassword}
                  onChange={(e) => {
                    handlePasswordInput(e);
                  }}
                  onClickIcon={() => {
                    handleToggle();
                  }}
                  onFocusInput={() => setIsErrorPassword(false)}
                />
              </div>
              <div className={styles.passwordConditions}>
                <div className={styles.conditionsTitle}>
                  <i className="totem-icon totem-icon-info-circle" /> <p>Votre mot de passe doit contenir :</p>
                </div>

                <div className={styles.conditionsDescription}>
                  <p className={mdpIsValidLength ? styles.conditionValid : styles.conditionError}>
                    {mdpIsValidLength ? (
                      <i className="totem-icon totem-icon-check-circle" />
                    ) : (
                      <i className="totem-icon totem-icon-cross-circle " />
                    )}
                    - 8 caractères dont au moins :
                  </p>

                  <p className={mdpIsValidCapital && mdpIsValidLower ? styles.conditionValid : styles.conditionError}>
                    {mdpIsValidCapital && mdpIsValidLower ? (
                      <i className="totem-icon totem-icon-check-circle" />
                    ) : (
                      <i className="totem-icon totem-icon-cross-circle " />
                    )}
                    - Une majuscule et une minuscule
                  </p>

                  <p className={mdpIsValidSpecialChar ? styles.conditionValid : styles.conditionError}>
                    {mdpIsValidSpecialChar ? (
                      <i className="totem-icon totem-icon-check-circle" />
                    ) : (
                      <i className="totem-icon totem-icon-cross-circle " />
                    )}
                    - Un caractère spécial (*, $, !, -...)
                  </p>

                  <p className={mdpIsValidDigit ? styles.conditionValid : styles.conditionError}>
                    {mdpIsValidDigit ? (
                      <i className="totem-icon totem-icon-check-circle" />
                    ) : (
                      <i className="totem-icon totem-icon-cross-circle " />
                    )}
                    - Un chiffre
                  </p>
                </div>
              </div>
            </div>

            <div>
              <TextInput
                label="Confirmez votre nouveau mot de passe"
                labelFor="confirm-mdp"
                placeholder="Votre nouveau mot de passe"
                type={typeConfirm}
                value={confirmPassword}
                img={(() => {
                  if (isErrorPassword) {
                    return 'totem-icon totem-icon-cross-circle';
                  }
                  return '';
                })()}
                icon={getPasswordIcon()}
                textError={errorText}
                isError={isErrorPassword}
                onChange={(e) => {
                  setConfirmPassword(e.currentTarget.value);
                }}
                onClickIcon={() => {
                  handleToggleConfirm();
                }}
                onFocusInput={() => setIsErrorPassword(false)}
              />
            </div>

            {pageKey === 'init' && (
              <p className={styles.cguText}>
                En cliquant sur “Je m’inscris”, je crée un compte client Meilleurtaux, conformément aux{' '}
                <a
                  href="https://placement.meilleurtaux.com/a-propos/conditions-generales-d-utilisation-du-site.html"
                  target="_blank"
                  className={styles.link}
                  rel="noreferrer noopener"
                >
                  conditions générales
                </a>{' '}
                et accepte que{' '}
                <a
                  href="https://placement.meilleurtaux.com/a-propos/politique-des-donnees-personnelles.html"
                  target="_blank"
                  className={styles.link}
                  rel="noreferrer noopener"
                >
                  mes données personnelles
                </a>{' '}
                soient partagées au sein de Groupe Meilleurtaux.
              </p>
            )}

            <div className={styles.confirmButton}>
              <MtxButton onClick={handleSubmit} variant="primary" size="large" full-width>
                <div>{pageKey === 'init' ? "Je m'inscris" : 'Valider'}</div>
              </MtxButton>
            </div>

            <MtxButton
              onClick={() => {
                handleCancel();
                navigate(ROUTE.LOGIN);
              }}
              variant="tertiary"
              size="large"
              full-width
            >
              <div>Annuler</div>
            </MtxButton>

            <input className="hide-element" type="submit" />
          </form>
        </MtxGrid>
      </MtxGrid>
    </MtxGrid>
  );
};
